/**
 * Loads a `.knytt.bin` world archive. Adapted from YKnyttLib.KnyttBinWorldLoader
 * (originally KnyttSharp by Codeusa).
 *
 * Format:
 *   "NF" header, null-terminated level name, uint32 file count, then per file:
 *   "NF", null-terminated path (\ -> /), uint32 LE size, raw bytes.
 * Files are keyed by lowercase path.
 */

const cp1252 = new TextDecoder('windows-1252');

export class BinLoader {
  readonly rootDirectory: string;
  private files = new Map<string, Uint8Array>();

  constructor(data: Uint8Array) {
    let pos = 0;
    const bytes = data;

    const readByte = (): number => bytes[pos++];
    const readNullString = (transformSlash: boolean): string => {
      const start = pos;
      while (pos < bytes.length && bytes[pos] !== 0) pos++;
      let s = cp1252.decode(bytes.subarray(start, pos));
      pos++; // skip null terminator
      if (transformSlash) s = s.replace(/\\/g, '/');
      return s;
    };
    const readUint32LE = (): number => {
      const v =
        bytes[pos] +
        bytes[pos + 1] * 256 +
        bytes[pos + 2] * 65536 +
        bytes[pos + 3] * 16777216;
      pos += 4;
      return v;
    };

    // Header
    if (bytes[0] !== 0x4e || bytes[1] !== 0x46) {
      throw new Error('Not a valid .knytt.bin file (missing NF header).');
    }
    pos = 2;
    this.rootDirectory = readNullString(false);
    readUint32LE(); // file count (unreliable, ignored)

    while (pos < bytes.length) {
      pos += 2; // skip "NF"
      const path = readNullString(true);
      const size = readUint32LE();
      const fileBytes = bytes.subarray(pos, pos + size);
      pos += size;
      this.files.set(path.toLowerCase(), fileBytes);
    }
  }

  getFile(name: string): Uint8Array | null {
    return this.files.get(name.toLowerCase()) ?? null;
  }

  has(name: string): boolean {
    return this.files.has(name.toLowerCase());
  }

  fileNames(): string[] {
    return [...this.files.keys()];
  }
}

/** Decompress a gzip stream (Map.bin) using the browser DecompressionStream API. */
export async function gunzip(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(ds);
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}
